import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  Alert,
  Divider,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import CustomCodeAgentPanel from './CustomCodeAgentPanel';

const DEFAULT_CODE = `async function run({ vars, helpers }) {
  // vars = global flow variables
  // helpers.llm(bindingId, { prompt, input, json? })
  // helpers.vision(bindingId, { prompt, fileVariable? })
  // helpers.http(bindingId, { body?, query?, headers?, pathParams? })
  // helpers.ops.run({ type, ... })
  return {
    result: vars.extractedText || null
  };
}
`;

const LLM_MODELS = [
  'gpt-4o', 'gpt-4o-mini', 'gpt-5', 'gpt-5-mini', 'gpt-5-nano',
  'gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-3.5-turbo',
  'gpt-5.1', 'gpt-5.6',
  'gemini-2.5-flash-lite', 'gemini-2.5-flash', 'gemini-2.0-flash',
  'gemini-3.1-flash-lite', 'gemini-3.5-flash-lite', 'gemini-3.5-flash', 'gemini-3.6-flash', 'gemini-3.7-flash',
  'anthropic-claude-sonnet-4', 'anthropic-claude-opus-4',
  'qwen-plus', 'qwen-max', 'qwen-turbo'
];

const VISION_METHODS = [
  { value: 'responses', label: 'OpenAI Responses' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'qwen', label: 'Alibaba Qwen' },
  { value: 'auto', label: 'Auto' }
];

const AUTH_TYPES = [
  { value: 'none', label: 'No Authentication' },
  { value: 'bearer_token', label: 'Bearer Token' },
  { value: 'basic_auth', label: 'Basic Auth' },
  { value: 'jwt_token', label: 'JWT Token' },
  { value: 'api_key', label: 'API Key' },
  { value: 'digest_auth', label: 'Digest Auth' },
  { value: 'oauth1', label: 'OAuth 1.0' },
  { value: 'oauth2', label: 'OAuth 2.0' }
];

function detectHelperIds(code) {
  const src = code || '';
  const llm = [...src.matchAll(/helpers\.llm\s*\(\s*['"]([^'"]+)['"]/g)].map(m => m[1]);
  const vision = [...src.matchAll(/helpers\.vision\s*\(\s*['"]([^'"]+)['"]/g)].map(m => m[1]);
  const http = [...src.matchAll(/helpers\.http\s*\(\s*['"]([^'"]+)['"]/g)].map(m => m[1]);
  return {
    llm: [...new Set(llm)],
    vision: [...new Set(vision)],
    http: [...new Set(http)]
  };
}

/**
 * Custom Code step configuration panel.
 */
export default function CustomCodeConfigPanel({
  step,
  onConfigChange,
  onCommit,
  projectId,
  companyId,
  flowData,
  availableVariables = []
}) {
  const [tab, setTab] = useState(0);
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);
  const config = step?.config || {};
  const code = config.code ?? DEFAULT_CODE;
  const outputVariables = Array.isArray(config.outputVariables) ? config.outputVariables : [];
  const bindings = config.bindings || { llm: [], vision: [], http: [] };
  const referenced = useMemo(() => detectHelperIds(code), [code]);

  const updateConfig = useCallback((patch) => {
    onConfigChange({ ...config, ...patch });
  }, [config, onConfigChange]);

  const setBindings = useCallback((next) => {
    updateConfig({ bindings: next });
  }, [updateConfig]);

  const addBinding = (kind) => {
    const list = [...(bindings[kind] || [])];
    const idBase = kind === 'llm' ? 'extract' : kind === 'vision' ? 'vision' : 'api';
    let id = idBase;
    let n = 1;
    while (list.some(b => b.id === id)) {
      id = `${idBase}${n++}`;
    }
    const blank =
      kind === 'llm'
        ? { id, llmModel: 'gpt-4o-mini' }
        : kind === 'vision'
          ? { id, visionMethod: 'responses', visionResponsesModel: 'gpt-4.1-mini' }
          : { id, method: 'GET', url: '', authType: 'none' };
    setBindings({ ...bindings, [kind]: [...list, blank] });
  };

  const removeBinding = (kind, index) => {
    const list = [...(bindings[kind] || [])];
    // Prefer removing by display index after placeholders exist in bindings via effect
    list.splice(index, 1);
    setBindings({ ...bindings, [kind]: list });
  };

  // Ensure referenced helper ids that are missing get placeholder binding panels
  React.useEffect(() => {
    const next = { ...bindings };
    let changed = false;
    for (const kind of ['llm', 'vision', 'http']) {
      const list = [...(next[kind] || [])];
      const existing = new Set(list.map(b => b.id));
      for (const id of referenced[kind]) {
        if (!existing.has(id)) {
          changed = true;
          if (kind === 'llm') list.push({ id, llmModel: 'gpt-4o-mini' });
          else if (kind === 'vision') list.push({ id, visionMethod: 'responses', visionResponsesModel: 'gpt-4.1-mini' });
          else list.push({ id, method: 'GET', url: '', authType: 'none' });
        }
      }
      next[kind] = list;
    }
    if (changed) {
      onConfigChange({ ...config, bindings: next });
    }
    // Only re-run when helper references in code change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenced.llm.join(','), referenced.vision.join(','), referenced.http.join(',')]);

  const llmBindings = bindings.llm || [];
  const visionBindings = bindings.vision || [];
  const httpBindings = bindings.http || [];

  // Show missing referenced ids as well (read-only merge for render)
  const displayLlm = (() => {
    const existing = new Set(llmBindings.map(b => b.id));
    const extras = referenced.llm.filter(id => !existing.has(id)).map(id => ({ id, llmModel: 'gpt-4o-mini' }));
    return [...llmBindings, ...extras];
  })();
  const displayVision = (() => {
    const existing = new Set(visionBindings.map(b => b.id));
    const extras = referenced.vision.filter(id => !existing.has(id)).map(id => ({
      id, visionMethod: 'responses', visionResponsesModel: 'gpt-4.1-mini'
    }));
    return [...visionBindings, ...extras];
  })();
  const displayHttp = (() => {
    const existing = new Set(httpBindings.map(b => b.id));
    const extras = referenced.http.filter(id => !existing.has(id)).map(id => ({
      id, method: 'GET', url: '', authType: 'none'
    }));
    return [...httpBindings, ...extras];
  })();

  const updateBinding = (kind, index, patch) => {
    const display =
      kind === 'llm' ? displayLlm : kind === 'vision' ? displayVision : displayHttp;
    const list = display.map((b, i) => (i === index ? { ...b, ...patch } : { ...b }));
    setBindings({ ...bindings, [kind]: list });
  };

  const applyAgentResult = (result) => {
    if (!result) return;
    updateConfig({
      code: result.code ?? code,
      outputVariables: result.outputVariables ?? outputVariables,
      bindings: result.bindings ?? bindings,
      timeoutMs: result.timeoutMs ?? config.timeoutMs ?? 30000
    });
    if (onCommit) onCommit();
  };

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Script" icon={<CodeIcon />} iconPosition="start" />
        <Tab label="AI Agent" />
      </Tabs>

      {tab === 1 ? (
        <CustomCodeAgentPanel
          projectId={projectId}
          companyId={companyId}
          flowData={flowData}
          step={step}
          availableVariables={availableVariables}
          onApply={applyAgentResult}
        />
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          <Alert severity="info">
            Scripts receive global <code>vars</code>. Declare outputs below. Configure LLM, Vision, and API
            models/auth on the node bindings — never hardcode them in the script.
          </Alert>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Editor
              height="280px"
              defaultLanguage="javascript"
              theme="vs-dark"
              value={code}
              onChange={(value) => updateConfig({ code: value ?? '' })}
              onMount={(editor) => {
                editor.onDidBlurEditorText(() => {
                  if (onCommitRef.current) onCommitRef.current();
                });
              }}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                scrollBeyondLastLine: false,
                automaticLayout: true
              }}
            />
          </Box>

          <TextField
            label="Timeout (ms)"
            type="number"
            size="small"
            value={config.timeoutMs ?? 30000}
            onChange={(e) => updateConfig({ timeoutMs: Number(e.target.value) || 30000 })}
            onBlur={onCommit}
            sx={{ maxWidth: 200 }}
          />

          <Typography variant="subtitle2">Output Variables</Typography>
          <Stack spacing={1}>
            {outputVariables.map((ov, i) => (
              <Box key={i} display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  label="Name"
                  value={ov.name || ''}
                  onChange={(e) => {
                    const next = [...outputVariables];
                    next[i] = { ...next[i], name: e.target.value };
                    updateConfig({ outputVariables: next });
                  }}
                  onBlur={onCommit}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={ov.type || 'string'}
                    onChange={(e) => {
                      const next = [...outputVariables];
                      next[i] = { ...next[i], type: e.target.value };
                      updateConfig({ outputVariables: next });
                    }}
                    onBlur={onCommit}
                  >
                    {['string', 'number', 'boolean', 'object', 'array', 'table'].map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  size="small"
                  onClick={() => {
                    const next = outputVariables.filter((_, idx) => idx !== i);
                    updateConfig({ outputVariables: next });
                    if (onCommit) onCommit();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                updateConfig({
                  outputVariables: [...outputVariables, { name: 'result', type: 'string' }]
                });
              }}
            >
              Add Output
            </Button>
          </Stack>

          <Divider />

          {/* LLM bindings */}
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">LLM Bindings</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => addBinding('llm')}>Add LLM</Button>
            </Box>
            {displayLlm.length === 0 && (
              <Typography variant="caption" color="text.secondary">None — add when the script calls helpers.llm</Typography>
            )}
            {displayLlm.map((b, i) => (
              <Accordion key={b.id || i} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Chip size="small" label={b.id || 'llm'} sx={{ mr: 1 }} />
                  <Typography variant="body2">LLM Model</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      size="small"
                      label="Binding ID"
                      value={b.id || ''}
                      onChange={(e) => updateBinding('llm', i, { id: e.target.value })}
                      onBlur={onCommit}
                      helperText="Used as helpers.llm('id', …)"
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>LLM Model</InputLabel>
                      <Select
                        label="LLM Model"
                        value={b.llmModel || 'gpt-4o-mini'}
                        onChange={(e) => updateBinding('llm', i, { llmModel: e.target.value })}
                        onBlur={onCommit}
                      >
                        {LLM_MODELS.map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button color="error" size="small" onClick={() => removeBinding('llm', i)}>Remove</Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Vision bindings */}
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">Vision AI Bindings</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => addBinding('vision')}>Add Vision AI</Button>
            </Box>
            {displayVision.map((b, i) => (
              <Accordion key={b.id || i} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Chip size="small" label={b.id || 'vision'} sx={{ mr: 1 }} />
                  <Typography variant="body2">Vision AI</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      size="small"
                      label="Binding ID"
                      value={b.id || ''}
                      onChange={(e) => updateBinding('vision', i, { id: e.target.value })}
                      onBlur={onCommit}
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>Vision Method</InputLabel>
                      <Select
                        label="Vision Method"
                        value={b.visionMethod || 'responses'}
                        onChange={(e) => updateBinding('vision', i, { visionMethod: e.target.value })}
                        onBlur={onCommit}
                      >
                        {VISION_METHODS.map(m => (
                          <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {(b.visionMethod === 'responses' || !b.visionMethod) && (
                      <FormControl fullWidth size="small">
                        <InputLabel>OpenAI Vision Model</InputLabel>
                        <Select
                          label="OpenAI Vision Model"
                          value={b.visionResponsesModel || 'gpt-4.1-mini'}
                          onChange={(e) => updateBinding('vision', i, { visionResponsesModel: e.target.value })}
                          onBlur={onCommit}
                        >
                          {['gpt-4o-mini', 'gpt-4.1-mini', 'gpt-5.1'].map(m => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {b.visionMethod === 'gemini' && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Gemini Vision Model</InputLabel>
                        <Select
                          label="Gemini Vision Model"
                          value={b.visionGeminiModel || 'gemini-2.5-flash-lite'}
                          onChange={(e) => updateBinding('vision', i, { visionGeminiModel: e.target.value })}
                          onBlur={onCommit}
                        >
                          {['gemini-2.5-flash-lite', 'gemini-2.5-flash', 'gemini-3.5-flash-lite', 'gemini-3.6-flash', 'gemini-3.7-flash'].map(m => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {b.visionMethod === 'qwen' && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Qwen Vision Model</InputLabel>
                        <Select
                          label="Qwen Vision Model"
                          value={b.visionQwenModel || 'qwen-vl-max-latest'}
                          onChange={(e) => updateBinding('vision', i, { visionQwenModel: e.target.value })}
                          onBlur={onCommit}
                        >
                          {[
                            'qwen3.8-flash',
                            'qwen3.8-max',
                            'qwen3.7-plus',
                            'qwen3.7-flash',
                            'qwen3-vl-plus',
                            'qwen3-vl-flash',
                            'qwen-vl-max-latest',
                            'qwen-vl-plus',
                            'qwen-vl-max'
                          ].map(m => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Button color="error" size="small" onClick={() => removeBinding('vision', i)}>Remove</Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* HTTP bindings */}
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">API Bindings</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => addBinding('http')}>Add API</Button>
            </Box>
            {displayHttp.map((b, i) => (
              <Accordion key={b.id || i} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Chip size="small" label={b.id || 'api'} sx={{ mr: 1 }} />
                  <Typography variant="body2">{b.method || 'GET'} {b.url || '(no URL)'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      size="small"
                      label="Binding ID"
                      value={b.id || ''}
                      onChange={(e) => updateBinding('http', i, { id: e.target.value })}
                      onBlur={onCommit}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Method</InputLabel>
                      <Select
                        label="Method"
                        value={b.method || 'GET'}
                        onChange={(e) => updateBinding('http', i, { method: e.target.value })}
                        onBlur={onCommit}
                      >
                        {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="API URL"
                      fullWidth
                      value={b.url || ''}
                      onChange={(e) => updateBinding('http', i, { url: e.target.value })}
                      onBlur={onCommit}
                      placeholder="https://api.example.com/endpoint"
                      helperText="Use {{variable}} for template substitution"
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>Authentication</InputLabel>
                      <Select
                        label="Authentication"
                        value={b.authType || 'none'}
                        onChange={(e) => updateBinding('http', i, { authType: e.target.value })}
                        onBlur={onCommit}
                      >
                        {AUTH_TYPES.map(a => (
                          <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {b.authType === 'bearer_token' && (
                      <TextField
                        size="small"
                        label="Bearer Token"
                        fullWidth
                        value={b.bearerToken || ''}
                        onChange={(e) => updateBinding('http', i, { bearerToken: e.target.value })}
                        onBlur={onCommit}
                        placeholder="{{apiToken}}"
                      />
                    )}
                    {b.authType === 'basic_auth' && (
                      <Box display="flex" gap={1}>
                        <TextField
                          size="small"
                          label="Username"
                          value={b.basicUsername || ''}
                          onChange={(e) => updateBinding('http', i, { basicUsername: e.target.value })}
                          onBlur={onCommit}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="Password"
                          type="password"
                          value={b.basicPassword || ''}
                          onChange={(e) => updateBinding('http', i, { basicPassword: e.target.value })}
                          onBlur={onCommit}
                          fullWidth
                        />
                      </Box>
                    )}
                    {b.authType === 'jwt_token' && (
                      <TextField
                        size="small"
                        label="JWT Token"
                        fullWidth
                        value={b.jwtToken || ''}
                        onChange={(e) => updateBinding('http', i, { jwtToken: e.target.value })}
                        onBlur={onCommit}
                      />
                    )}
                    {b.authType === 'api_key' && (
                      <>
                        <TextField
                          size="small"
                          label="API Key"
                          fullWidth
                          value={b.apiKey || ''}
                          onChange={(e) => updateBinding('http', i, { apiKey: e.target.value })}
                          onBlur={onCommit}
                        />
                        <TextField
                          size="small"
                          label="Header / Param Name"
                          fullWidth
                          value={b.apiKeyHeader || b.apiKeyName || 'X-API-Key'}
                          onChange={(e) => updateBinding('http', i, { apiKeyHeader: e.target.value })}
                          onBlur={onCommit}
                        />
                      </>
                    )}
                    {b.authType === 'oauth2' && (
                      <TextField
                        size="small"
                        label="OAuth2 Access Token"
                        fullWidth
                        value={b.oauth2AccessToken || ''}
                        onChange={(e) => updateBinding('http', i, { oauth2AccessToken: e.target.value })}
                        onBlur={onCommit}
                      />
                    )}
                    <Button color="error" size="small" onClick={() => removeBinding('http', i)}>Remove</Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Alert severity="success" icon={false}>
            <Typography variant="caption" component="div">
              <strong>Helper cheat-sheet</strong>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 11 }}>
{`await helpers.llm('extract', { prompt, input, json: true })
await helpers.vision('stamp', { prompt, fileVariable: 'uploadedFiles' })
await helpers.http('webhook', { body: { ... } })
await helpers.ops.run({ type: 'filter_array', ... })`}
              </pre>
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
}
